
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { UserRightsMixin } from '@/mixins/UserRights';
import Project from '@/models/Project';
import InternalOrder from './InternalOrder.vue';
import Offer from '@/models/Offer';
// import InternalOrder from '@/models/InternalOrder';
import { PDFAttachment } from '@/interfaces/components/projectNew/InternalOrderPDFAttachment';
import { dateTrackerConfiguration } from '@/helpers/OrderHelper';
import { formatDateToDateString, isDateEarlierOrSameAsToday } from '@/helpers/LocaleHelper';
// @ts-ignore
import downloadFile from 'downloadjs';
import OrderPdf from '@/models/OrderPdf';
import { onDownloadPDF } from '@/helpers/OfferHelper';
import { calculateLatestDate, downloadOrderPDF } from '@/helpers/OrderHelper';

@Component({
    name: 'InternalOrdersTracker',
    components: {
        InternalOrder,
    },
})
export default class InternalOrdersTracker extends mixins<UserRightsMixin>(UserRightsMixin) {
    @Prop({ required: true }) private project!: Project | null;
    @Prop({ required: true }) private selectedOffer!: Offer | null;
    @Prop({ required: true }) private isCurrentUserTechnologist!: boolean;
    @Prop({ required: true }) private internalOrders!: any[];
    @Prop({ default: false }) private customDateTitles!: { manufactureDate?: string; deliveryDate?: string };
    @Prop({ required: true }) private isProjectImported!: boolean;
    @Prop({ required: false }) private orderNumber!: string;

    private get statuses() {
        const statuses = dateTrackerConfiguration.statuses.slice();

        if (this.isProjectImported) {
            // Remove offerCreated status for imported projects
            return statuses.filter((status) => status.name !== 'offerCreated');
        }

        return statuses;
    }

    private orderRowsVisible = true;

    private selectAvailableDate(dates: any): string {
        if (dates?.abConfirmedDate) {
            return formatDateToDateString(dates.abConfirmedDate);
        } else if (dates?.inProduction) {
            return formatDateToDateString(dates.finalDate);
        }

        return '-';
    }

    private get isPreorderOriginated(): boolean {
        if (this.selectedOffer?.technologistOrders?.length) {
            return this.selectedOffer.technologistOrders.some((order: any) => !!order.projectPreorder);
        }
        return false;
    }
    
    private getLatestDate(status: any) {
        if (status.name === 'offerCreated') {
            if (this.isPreorderOriginated && this.project?.projectPreorder?.createdAt) {
                return formatDateToDateString(this.project.projectPreorder.createdAt);
            }
            if (this.selectedOffer) {
                return formatDateToDateString(this.selectedOffer.createdAt);
            }
            if (this.project?.hasPendingPreorder && this.project?.projectPreorder?.createdAt) {
                return formatDateToDateString(this.project.projectPreorder.createdAt);
            }
            return '-';
        }

        const dates = this.internalOrders.map((order: any) => {
            let orderDate = order[status.name];

            if (!orderDate || orderDate === '-') {
                return false;
            }

            return orderDate;
        });

        const latestDate = calculateLatestDate(dates);
        if (status.name === 'deliveryDate' && (!latestDate || latestDate === '-')) {
            return formatDateToDateString(this.selectedOffer?.requestedDeliveryDate) || '-';
        }

        return latestDate;
    }

    private getStatusDescription(status: any): string {
        if (status.name === 'orderProduction') {
            return this.customDateTitles?.manufactureDate || ('Estimated manufacture date' as string);
        } else if (status.name === 'deliveryDate') {
            return this.customDateTitles?.deliveryDate || ('Estimated delivery date' as string);
        } else if (status.name === 'offerCreated' && this.isPreorderOriginated) {
            return 'Preorder created';
        } else if (status.name === 'offerCreated' && !this.selectedOffer && this.project?.hasPendingPreorder) {
            return 'Preorder created';
        } else {
            return status.description;
        }
    }

    private getInfoBoxContent(status: any): string {
        if (!status?.infoBox) {
            return '';
        }

        return this.$t(status.infoBox) as string;
    }

    private triggerRowVisibility() {
        this.orderRowsVisible = !this.orderRowsVisible;
    }

    private get client() {
        if (!this.project) {
            return null;
        }

        return this.project?.client;
    }

    private get hasOfferPDF() {
        return !!(this.selectedOffer && this.selectedOffer.pdf);
    }

    private get hasOrderConfirmationPDF() {
        return !!(this.selectedOffer && this.selectedOffer.orderPdf);
    }

    private get hasOrderPDF() {
        return !!(this.selectedOffer && this.selectedOffer.orderPdf);
    }

    private get isOfferCompleted() {
        return !!this.project && this.selectedOffer !== null && this.project.state !== 'lead';
    }

    private get isOrderCompleted() {
        return this.isOfferCompleted && this.project!.state !== 'offer';
    }

    private get orderId() {
        const orderId = this.project?.orderNumber;

        if (!orderId) {
            return this.internalOrders.length > 0 ? this.internalOrders[0].id : this.$t('-');
        }

        return orderId || '-';
    }

    private get firstInternalOrder() {
        if (this.internalOrders.length > 0) {
            return this.internalOrders[0].id;
        }

        return '-';
    }

    private getInternalOrderDate(order: any, statusName: string) {
        // Differentiate logic bewteen deliveryDate and estimatedDeliveryDate
        let orderDate = order[statusName];

        return orderDate;
    }

    private get offerCreatedRowIsPreorder(): boolean {
        return this.isPreorderOriginated || (!this.selectedOffer && !!this.project?.projectPreorder);
    }

    private get hasPreorderPDF(): boolean {
        return !!this.project?.preorderPdfUrl;
    }

    private hasPDF(orderState: string): boolean {
        switch (orderState) {
        case 'offerCreated':
            return this.offerCreatedRowIsPreorder ? this.hasPreorderPDF : this.hasOfferPDF;
        case 'orderCreated':
            return this.hasOrderPDF;
        default:
            return false;
        }
    }

    private downloadPDF(order: any) {
        if (!this.hasPDF(order)) {
            return;
        }

        if (this.isCurrentUserTechnologist) {
            this.$notification.error({
                message: this.$t('You do not have permission to download the PDF') as string,
                description: '',
            });
            return;
        }

        switch (order) {
            case 'offerCreated':
                if (this.offerCreatedRowIsPreorder) {
                    this.downloadPreorderPDF();
                } else {
                    this.downloadOfferPDF();
                }
                break;
            case 'orderCreated':
                this.downloadOrderPDF();
            break;
            default:
                return;
        }
    }

    private async downloadPreorderPDF() {
        const pdfUrl = this.project?.preorderPdfUrl;
        if (!pdfUrl) {
        return;
        }
        try {
        const pdfData = await OrderPdf.getInternalOrderPDF(pdfUrl);
        downloadFile(pdfData.data, `Preorder-${this.project?.id}.pdf`);
        } catch (e) {
        this.$notification.error({
            message: this.$t('Error downloading PDF') as string,
            description: (e as Error).message,
        });
        return;
        }
        this.$notification.success({
        message: this.$t('Preorder PDF successfully downloaded') as string,
        description: '',
        });
    }

    private async downloadOfferPDF() {
        if (!this.selectedOffer) {
            return;
        }

        try {
            await onDownloadPDF(this.selectedOffer, this.client);
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error downloading PDF') as string,
                description: (e as Error).message,
            });
            return;
        }

        this.$notification.success({
            message: this.$t('Offer PDF successfully downloaded') as string,
            description: '',
        });
    }

    private async downloadOrderPDF() {
        if (!this.selectedOffer) {
            return;
        }

        try {
            await downloadOrderPDF(this.selectedOffer, this.client);
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error downloading PDF') as string,
                description: (e as Error).message,
            });
            return;
        }
        this.$notification.success({
            message: this.$t('Order PDF successfully downloaded') as string,
            description: '',
        });
    }

    private async downloadInternalOrderPDF(attachment: PDFAttachment) {
        try {
            const pdfData = await OrderPdf.getInternalOrderPDF(attachment.attachment);

            downloadFile(pdfData.data, attachment.displayName);
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error downloading PDF') as string,
                description: (e as Error).message,
            });
            return;
        }

        this.$notification.success({
            message: this.$t(`${attachment.title} PDF successfully downloaded`) as string,
            description: '',
        });
    }

    private get progressFillHeight(): number {
        if (this.statuses.length === 0) return 0;

        // Find the index of the last status that has at least one date
        let lastCompletedIndex = -1;
        for (let i = this.statuses.length - 1; i >= 0; i--) {
            if (this.atLeastOneDateIsReached(this.statuses[i])) {
                lastCompletedIndex = i;
                break;
            }
        }

        if (lastCompletedIndex === -1) return 0; // No colored circles

        // Calculate percentage based on last completed status index
        return ((lastCompletedIndex + 1) / this.statuses.length) * 100;
    }

    private atLeastOneDateIsPresent(status: any): boolean {
        const oneDateIsPresent = this.internalOrders.some((order) => {
            let orderDate = order[status.name];

            return orderDate && orderDate !== '-';
        });

        return oneDateIsPresent;
    }

    private atLeastOneDateIsReached(status: any): boolean {
        if (!this.atLeastOneDateIsPresent(status)) {
            // Check next status if current one is unavailable
            const currentStatusIndex = this.statuses.findIndex((s) => s.name === status.name);

            if (currentStatusIndex < this.statuses.length - 1) {
                const nextStatus = this.statuses[currentStatusIndex + 1];
                return this.atLeastOneDateIsReached(nextStatus);
            }
        }

        const oneDateIsReached = this.internalOrders.some((order) => {
            let orderDate = order[status.name];

            if (!orderDate || orderDate === '-') {
                return '';
            }

            return isDateEarlierOrSameAsToday(orderDate);
        });

        return oneDateIsReached;
    }

    private allDatesAreReached(status: any): boolean {
        if (this.internalOrders.length === 0) {
            return false;
        }

        return this.internalOrders.every((order) => {
            let orderDate = order[status.name];

            if (!orderDate || orderDate === '-') {
                return false;
            }

            // Check if the date is before the current date
            return isDateEarlierOrSameAsToday(orderDate);
        });
    }
}
